import React, { useRef, useState } from 'react';
import '../styles/components/photoReplacement.css';

interface PhotoReplacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoSelected: (file: File, scope: 'one' | 'all') => void;
  itemCount: number;
}

const PhotoReplacementModal: React.FC<PhotoReplacementModalProps> = ({
  isOpen,
  onClose,
  onPhotoSelected,
  itemCount
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [scope, setScope] = useState<'one' | 'all'>('one');

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onPhotoSelected(file, scope);
      onClose();
    }
  };

  return (
    <div className="photo-replacement-overlay" onClick={onClose}>
      <div className="photo-replacement-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Replace Photo</h3>
        <p>Choose a new photo for this item.</p>

        {/* Scope toggle - only show if multiple items */}
        {itemCount > 1 && (
          <div className="photo-replacement-toggle">
            <button
              className={`photo-replacement-toggle-btn ${scope === 'one' ? 'active' : ''}`}
              onClick={() => setScope('one')}
            >
              This item only
            </button>
            <button
              className={`photo-replacement-toggle-btn ${scope === 'all' ? 'active' : ''}`}
              onClick={() => setScope('all')}
            >
              All {itemCount} items
            </button>
          </div>
        )}

        <div className="photo-replacement-actions">
          {/* Camera option */}
          <button
            className="photo-replacement-btn photo-replacement-btn-camera"
            onClick={() => cameraInputRef.current?.click()}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
            <span>Take Photo</span>
          </button>

          {/* Photo Library option */}
          <button
            className="photo-replacement-btn photo-replacement-btn-library"
            onClick={() => fileInputRef.current?.click()}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <span>Choose from Library</span>
          </button>

          {/* Cancel button */}
          <button
            className="photo-replacement-btn photo-replacement-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>
    </div>
  );
};

export default PhotoReplacementModal;
